
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// module components
import WabaCreateTemplateModal from '@/modules/NotificationsModule/components/WabaCreateTemplateModal/index.vue'
import WabaUpdateTemplateModal from '@/modules/NotificationsModule/components/WabaUpdateTemplateModal/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import type { SettingsWabaTemplate, SettingsWabaTemplatesCategory, SettingsWabaTemplatesLanguage, SettingsWabaTemplatesStatus } from '@/core/types/settings.types'

// composable
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

// realisations
import { settings } from '@/core/realisations/settings.realisation'

// @helpers
import cut from '@/common/helpers/cut'

const TABLE_COLUMNS = [
    {
        name: 'ID',
        key: 'id',
    },
    {
        name: 'Название уведомления',
        key: 'name',
    },
    {
        name: 'Категория',
        key: 'category',
    },
    {
        name: 'Статус',
        key: 'status',
    },
    {
        name: 'Текст',
        key: 'text',
        withHint: true,
    },
]

export default defineComponent({
    components: {
        PageTitle,
        FailedFetch,
        TheInput,
        TheSelect,
        TheButton,
        NewTable,
        NewPagination,
        NewLimit,
        Loader,
        WabaCreateTemplateModal,
        WabaUpdateTemplateModal,
    },
    setup() {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            is_create_modal_opened: false,
            is_update_modal_opened: false,
            update_modal_template: null as SettingsWabaTemplate | null,
            is_delete_loading: false,
            is_sync_loading: false,
            is_shared_sync_loading: false,
        })

        const filters = reactive({
            search: null as string | null,
            status: null as string | null,
            language: null as string | null,
            category: null as string | null,
        })

        const data_state = reactive({
            templates: new DataState<SettingsWabaTemplate>({}),
            languages: new DataState<SettingsWabaTemplatesLanguage>({}),
            statuses: new DataState<SettingsWabaTemplatesStatus>({}),
            categories: new DataState<SettingsWabaTemplatesCategory>({}),
        })

        const loadTemplates = async (page: number) => {
            data_state.templates.pagination.current_page = page

            try {
                data_state.templates.is_loading = true

                const response = await settings.getWabaTemplates({
                    page: data_state.templates.pagination.current_page,
                    limit: data_state.templates.pagination.limit,
                    search: filters.search,
                    status: filters.status,
                    language: filters.language,
                    category: filters.category
                })

                data_state.templates.list = response.data

                data_state.templates.pagination.total_pages = response.meta.last_page
                data_state.templates.pagination.total_items = response.meta.total

                data_state.templates.is_failed = false
            } catch (error) {
                data_state.templates.failure.catchError(error)
                data_state.templates.is_failed = true
            } finally {
                data_state.templates.is_loading = false
            }
        }

        const loadLanguages = async () => {
            try {
                data_state.languages.is_loading = true

                const response = await settings.wabaTemplatesGetLanguages()
                data_state.languages.list = response.data

                data_state.languages.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.languages.is_failed = true
            } finally {
                data_state.languages.is_loading = false
            }
        }

        const loadStatuses = async () => {
            try {
                data_state.statuses.is_loading = true

                const response = await settings.wabaTemplatesGetStatuses()
                data_state.statuses.list = response.data

                data_state.statuses.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.statuses.is_failed = true
            } finally {
                data_state.statuses.is_loading = false
            }
        }

        const loadCategories = async () => {
            try {
                data_state.categories.is_loading = true

                const response = await settings.wabaTemplatesGetCategories()
                data_state.categories.list = response.data

                data_state.categories.is_failed = false
            } catch (error) {
                console.error(error)
                data_state.categories.is_failed = true
            } finally {
                data_state.categories.is_loading = false
            }
        }

        const onLimitChange = (limit: number) => {
            data_state.templates.pagination.limit = limit
            loadTemplates(data_state.templates.pagination.current_page)
        }

        const onChangePage = (page: number) => {
            data_state.templates.pagination.current_page = page
            loadTemplates(page)
        }

        onBeforeMount(() => {
            data_state.templates.pagination.limit = 50
            loadTemplates(1)
            loadStatuses()
            loadLanguages()
            loadCategories()
        })

        const onCreate = () => {
            state.is_create_modal_opened = true
        }

        const onCreateCancel = () => {
            state.is_create_modal_opened = false
        }

        const onCreated = () => {
            state.is_create_modal_opened = false
            loadTemplates(data_state.templates.pagination.current_page)
        }

        const onUpdate = (template: SettingsWabaTemplate) => {
            state.update_modal_template = template
            state.is_update_modal_opened = true
        }

        const onUpdateCancel = () => {
            state.is_update_modal_opened = false
            state.update_modal_template = null
        }

        const onUpdated = () => {
            state.is_update_modal_opened = false
            state.update_modal_template = null
            loadTemplates(data_state.templates.pagination.current_page)
        }

        const onDelete = async (template: SettingsWabaTemplate) => {
            const answer = confirm(`Вы действительно хотите удалить шаблон "${template.name}"?`)
            if (!answer) return

            try {
                state.is_delete_loading = true

                await settings.deleteWabaTemplate(template.id)
                notifications.notify({ type: 'success', title: `Шаблон "${template.name}" удален` })

                loadTemplates(data_state.templates.pagination.current_page)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При удалении шаблона произошла ошибка' })
            } finally {
                state.is_delete_loading = false
            }
        }

        const onSync = async (template: SettingsWabaTemplate) => {
            const answer = confirm(`Вы действительно хотите синхронизировать шаблон "${template.name}"?`)
            if (!answer) return

            try {
                state.is_sync_loading = true

                await settings.syncWabaTemplate(template.id)
                notifications.notify({ type: 'success', title: `Шаблон "${template.name}" синхронизирован` })

                loadTemplates(data_state.templates.pagination.current_page)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При синхронизации шаблона произошла ошибка' })
            } finally {
                state.is_sync_loading = false
            }
        }

        const onSharedSync = async () => {
            if (state.is_shared_sync_loading) return

            const answer = confirm('Вы действительно хотите синхронизировать все шаблоны?')
            if (!answer) return

            try {
                state.is_shared_sync_loading = true

                await settings.syncWabaTemplates()
                notifications.notify({ type: 'success', title: 'Шаблоны синхронизированы' })

                loadTemplates(data_state.templates.pagination.current_page)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При синхронизации шаблонов произошла ошибка' })
            } finally {
                state.is_shared_sync_loading = false
            }
        }

        const reset = () => {
            filters.search = null
            filters.status = null
            filters.language = null
            filters.category = null

            loadTemplates(1)
        }

        //

        const isCanCreate = computed(() => havePermissions(['waba-template.create', 'all-root-permission']))
        const isCanUpdate = computed(() => havePermissions(['waba-template.update', 'all-root-permission']))
        const isCanDelete = computed(() => havePermissions(['waba-template.delete', 'all-root-permission']))
        const isCanSync = computed(() => havePermissions(['waba-template.sync-status', 'all-root-permission']))
        const isCanSharedSync = computed(() => havePermissions(['waba-template.sync-templates', 'all-root-permission']))

        return {
            state,
            filters,
            data_state,
            loadTemplates,
            loadLanguages,
            loadStatuses,
            loadCategories,
            onLimitChange,
            onChangePage,
            TABLE_COLUMNS,
            cut,
            reset,
            //
            onCreate,
            onCreateCancel,
            onCreated,
            onUpdate,
            onUpdateCancel,
            onUpdated,
            onDelete,
            onSync,
            onSharedSync,
            //
            isCanCreate,
            isCanUpdate,
            isCanDelete,
            isCanSync,
            isCanSharedSync
        }
    },
})
